import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { Box, Stack, Typography } from "@mui/material";

import BusinessIcon from "@mui/icons-material/Business";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import HubOutlinedIcon from "@mui/icons-material/HubOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import StorageOutlinedIcon from "@mui/icons-material/StorageOutlined";
import RuleOutlinedIcon from "@mui/icons-material/RuleOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import AdminPanelSettingsOutlinedIcon from "@mui/icons-material/AdminPanelSettingsOutlined";
import PaidOutlinedIcon from "@mui/icons-material/PaidOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import useAuth from "../../hooks/useAuth";
import useOrganisation from "../../hooks/super/useOrganisation";
import useUser from "../../hooks/super/useUser";

import PageHeader from "../../components/pages/_new/super/common/PageHeader";
import {
  TOKENS,
  StatTile,
  SectionCard,
} from "../../components/pages/_new/super/design";

const ShortcutCard = ({ icon, title, body, to }) => {
  const navigate = useNavigate();
  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => navigate(to)}
      onKeyDown={(e) => {
        if (e.key === "Enter") navigate(to);
      }}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        p: 2,
        borderRadius: `${TOKENS.radiusS}px`,
        border: `1px solid ${TOKENS.border}`,
        background: TOKENS.bgSurface,
        cursor: "pointer",
        transition: "border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease",
        "&:hover": {
          borderColor: TOKENS.primary,
          boxShadow: TOKENS.shadowS,
          transform: "translateY(-1px)",
        },
        "&:focus-visible": {
          outline: "none",
          boxShadow: TOKENS.shadowFocus,
          borderColor: TOKENS.primary,
        },
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: `${TOKENS.radiusXs}px`,
          background: TOKENS.primaryTint,
          color: TOKENS.primaryInk,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 600, color: TOKENS.fg1 }}>{title}</Typography>
        <Typography sx={{ fontSize: 12.5, color: TOKENS.fg2, mt: 0.25 }}>{body}</Typography>
      </Box>
      <ArrowForwardIcon sx={{ fontSize: 18, color: TOKENS.fg3, alignSelf: "center" }} />
    </Box>
  );
};

function SAHome() {
  const { isAuthenticated, user } = useAuth();
  const { organisations, getOrganisations } = useOrganisation();
  const { admins, getAdmins } = useUser();

  React.useEffect(() => {
    if (isAuthenticated) {
      getOrganisations();
      getAdmins();
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  const orgCount = Array.isArray(organisations) ? organisations.length : 0;
  const adminCount = Array.isArray(admins) ? admins.length : 0;
  const activeAdmins = Array.isArray(admins) ? admins.filter((a) => !a?.isDeleted).length : 0;
  const unverifiedAdmins = Array.isArray(admins) ? admins.filter((a) => !a?.isVerified).length : 0;

  const greetingName =
    `${user?.firstName || ""}`.trim() ||
    (user?.email ? String(user.email).split("@")[0] : "Administrator");

  return (
    <React.Fragment>
      <Helmet title="SA Home" />
      <PageHeader
        title={`Welcome back, ${greetingName}`}
        subtitle="Quick jump into the most-used super-admin tools"
      />

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
          gap: 2,
          mb: 3,
        }}
      >
        <StatTile
          icon={<BusinessIcon />}
          value={orgCount}
          label="Organisations"
          sub="Across all regions"
        />
        <StatTile
          icon={<PeopleAltOutlinedIcon />}
          iconBg={TOKENS.successBg}
          iconInk={TOKENS.successInk}
          value={activeAdmins}
          label="Active administrators"
          sub={adminCount ? `of ${adminCount} total` : undefined}
        />
        <StatTile
          icon={<HubOutlinedIcon />}
          iconBg={TOKENS.infoBg}
          iconInk={TOKENS.infoInk}
          value="—"
          label="Healthy engines"
          sub="See Engines for live status"
        />
        <StatTile
          icon={<NotificationsActiveOutlinedIcon />}
          iconBg={TOKENS.warningBg}
          iconInk={TOKENS.warningInk}
          value={unverifiedAdmins}
          label="Pending verifications"
          sub="Unverified admin accounts"
        />
      </Box>

      <Stack spacing={2}>
        <SectionCard title="Quick actions" description="Jump straight into the most common operator tasks">
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: 1.5,
            }}
          >
            <ShortcutCard
              icon={<BusinessIcon fontSize="small" />}
              title="Organisations"
              body="Manage tenants and plans"
              to="/super/application/organisation"
            />
            <ShortcutCard
              icon={<AdminPanelSettingsOutlinedIcon fontSize="small" />}
              title="Administrators"
              body="Invite or verify staff accounts"
              to="/super/application/user/admins"
            />
            <ShortcutCard
              icon={<HubOutlinedIcon fontSize="small" />}
              title="Engines"
              body="WAF / BM / DDoS / AU engine status"
              to="/super/application/monitor"
            />
            <ShortcutCard
              icon={<LockOutlinedIcon fontSize="small" />}
              title="SSL"
              body="Certificate provisioning + Zero SSL key"
              to="/super/application/ssl"
            />
            <ShortcutCard
              icon={<ShieldOutlinedIcon fontSize="small" />}
              title="Anti-DDoS"
              body="Config, exceptions, active blocks"
              to="/super/application/ad/config"
            />
            <ShortcutCard
              icon={<RuleOutlinedIcon fontSize="small" />}
              title="Custom rules"
              body="WAF rule authoring"
              to="/super/application/rule/custom"
            />
            <ShortcutCard
              icon={<VpnKeyOutlinedIcon fontSize="small" />}
              title="API keys"
              body="OTX, AbuseIPDB"
              to="/super/application/apikey/otx"
            />
            <ShortcutCard
              icon={<PaidOutlinedIcon fontSize="small" />}
              title="Payment plans"
              body="Common, custom, features"
              to="/super/application/payment/common"
            />
            <ShortcutCard
              icon={<StorageOutlinedIcon fontSize="small" />}
              title="ClickHouse"
              body="Endpoint, auth, cloud toggle"
              to="/super/application/clickhouse"
            />
            <ShortcutCard
              icon={<SettingsOutlinedIcon fontSize="small" />}
              title="Email & notifications"
              body="SMTP templates, global banners"
              to="/super/application/general/email"
            />
            <ShortcutCard
              icon={<RuleOutlinedIcon fontSize="small" />}
              title="SmartParse"
              body="Per-org tuning"
              to="/super/application/smartparse"
            />
            <ShortcutCard
              icon={<SettingsOutlinedIcon fontSize="small" />}
              title="Profile"
              body="Update your password"
              to="/super/application/profile"
            />
          </Box>
        </SectionCard>

        <SectionCard title="At a glance" description="A compact snapshot of platform health">
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} sx={{ flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 220, p: 1.5, borderRadius: `${TOKENS.radiusXs}px`, border: `1px solid ${TOKENS.borderSubtle}`, background: TOKENS.bgSunken }}>
              <Typography sx={{ fontSize: 11.5, color: TOKENS.fg3, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
                Recent organisation
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: TOKENS.fg1, mt: 0.5, fontWeight: 600 }}>
                {organisations?.[0]?.title || "—"}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 220, p: 1.5, borderRadius: `${TOKENS.radiusXs}px`, border: `1px solid ${TOKENS.borderSubtle}`, background: TOKENS.bgSunken }}>
              <Typography sx={{ fontSize: 11.5, color: TOKENS.fg3, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
                Most recent admin
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: TOKENS.fg1, mt: 0.5, fontWeight: 600 }}>
                {(admins?.[0]?.firstName || "") + " " + (admins?.[0]?.lastName || "") || admins?.[0]?.email || "—"}
              </Typography>
            </Box>
            <Box sx={{ flex: 1, minWidth: 220, p: 1.5, borderRadius: `${TOKENS.radiusXs}px`, border: `1px solid ${TOKENS.borderSubtle}`, background: TOKENS.bgSunken }}>
              <Typography sx={{ fontSize: 11.5, color: TOKENS.fg3, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
                Your role
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: TOKENS.fg1, mt: 0.5, fontWeight: 600 }}>
                {user?.role !== undefined ? `Role ${user.role}` : "Super Admin"}
              </Typography>
            </Box>
          </Stack>
        </SectionCard>
      </Stack>
    </React.Fragment>
  );
}
export default SAHome;
